import { useApolloClient, useMutation, useQuery } from "@apollo/client";
import { Stack, Typography } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { IssueListButton } from "components/Forms/IssueListButton";
import { Issue, SubmissionStepType } from "generated-graphql/graphql";
import { useValidatingForm } from "hooks/useValidatingForm";
import { useWasteStepper } from "hooks/useWasteStepper";
import { useCallback, useEffect, useMemo } from "react";
import { FormProvider } from "react-hook-form";
import {
  useFacilityRcraProfile,
  useUpsertFacilityRcraProfile,
} from "routes/Customer/Facility/useFacility";
import { hasCriticalIssues } from "util/forms";

import {
  CLEAR_SUBMISSION_RCRA_EPISODIC_EVENT,
  CLEAR_SUBMISSION_RCRA_HSM_NOTIFICATION,
  CLEAR_SUBMISSION_RCRA_SITE_CLOSURE,
  GET_SUBMISSION_FILING_NOTIFICATIONS_QUERY,
  UPSERT_SUBMISSION_RCRA_CONSOLIDATION,
  UPSERT_SUBMISSION_RCRA_EPISODIC_EVENT,
  UPSERT_SUBMISSION_RCRA_HSM_NOTIFICATION,
  UPSERT_SUBMISSION_RCRA_SITE_CLOSURE,
  VALIDATE_SUBMISSION_RCRA_FILING_NOTIFICATIONS,
} from "../queries";
import { StepHeader } from "../StepHeader";
import {
  buildClosureInput,
  buildConsolidationGateInput,
  buildConsolidationInput,
  buildDefaultValues,
  buildEpisodicInput,
  buildHsmInput,
} from "./FilingNotifications/buildInputs";
import { EpisodicGenerationSection } from "./FilingNotifications/EpisodicGenerationSection";
import { HsmNotificationSection } from "./FilingNotifications/HsmNotificationSection";
import { LqgConsolidationSection } from "./FilingNotifications/LqgConsolidationSection";
import { LqgSiteClosureSection } from "./FilingNotifications/LqgSiteClosureSection";
import {
  FilingNotificationsFormData,
  GetSubmissionFilingNotificationsResult,
} from "./FilingNotifications/types";
import { StepSaveFooter } from "./StepSaveFooter";

/**
 * Filing Notifications stepper step — the optional 8700-12 notification sections
 * (§13 episodic generation, §14 LQG consolidation, §15 LQG site closure, §16 HSM
 * notification). Each section has a Yes/No gate; answering "Yes" reveals its
 * fields (and, for §13/§14/§16, a repeatable child table). On save, every section
 * persists independently: the gate=Yes sections upsert via their mutation, the
 * gate=No (or unanswered) sections clear via their `clear...` mutation.
 *
 * §14 LQG Consolidation splits its persistence across two scopes: the
 * `isLqgConsolidator` gate stays on the facility's RCRA profile (read via
 * `useFacilityRcraProfile`, saved via a narrow `upsertFacilityRcraProfile`
 * touching only that field), while the consolidated sites are submission-scoped
 * (read from `submission.consolidatedSites`, saved via
 * `upsertSubmissionRcraConsolidation`). §14 validation runs through the
 * submission-scoped consolidation validator, which takes both the gate and the
 * sites.
 *
 * Frozen submissions never reach this editor; read-only customers (`!canEdit`)
 * see the sections display-only with no save action.
 */
export function FilingNotificationsStep({
  submissionId,
  facilityId,
}: {
  submissionId: string;
  facilityId: string;
}) {
  const alerts = useAlerts();
  const { canEdit } = useWasteStepper();
  const isDisplayOnly = !canEdit;

  const { data } = useQuery<GetSubmissionFilingNotificationsResult>(
    GET_SUBMISSION_FILING_NOTIFICATIONS_QUERY,
    {
      variables: { id: submissionId },
      fetchPolicy: "cache-and-network",
    }
  );

  // §14's `isLqgConsolidator` gate lives on the facility's RCRA profile, so it
  // loads from (and saves to) that profile; the sites are submission-scoped
  // (loaded via the filing-notifications query, saved via the consolidation
  // mutation).
  const { data: rcraData } = useFacilityRcraProfile(facilityId);
  const profile = rcraData?.facility?.facilityRcraProfile ?? null;
  const [upsertRcraProfile, { loading: savingGate }] = useUpsertFacilityRcraProfile();
  const [upsertConsolidation, { loading: savingConsolidation }] = useMutation(
    UPSERT_SUBMISSION_RCRA_CONSOLIDATION
  );

  const [upsertEpisodic, { loading: savingEpisodic }] = useMutation(
    UPSERT_SUBMISSION_RCRA_EPISODIC_EVENT
  );
  const [clearEpisodic, { loading: clearingEpisodic }] = useMutation(
    CLEAR_SUBMISSION_RCRA_EPISODIC_EVENT
  );
  const [upsertClosure, { loading: savingClosure }] = useMutation(
    UPSERT_SUBMISSION_RCRA_SITE_CLOSURE
  );
  const [clearClosure, { loading: clearingClosure }] = useMutation(
    CLEAR_SUBMISSION_RCRA_SITE_CLOSURE
  );
  const [upsertHsm, { loading: savingHsm }] = useMutation(UPSERT_SUBMISSION_RCRA_HSM_NOTIFICATION);
  const [clearHsm, { loading: clearingHsm }] = useMutation(CLEAR_SUBMISSION_RCRA_HSM_NOTIFICATION);

  const saving =
    savingEpisodic ||
    clearingEpisodic ||
    savingClosure ||
    clearingClosure ||
    savingHsm ||
    clearingHsm ||
    savingGate ||
    savingConsolidation;

  const defaultValues = useMemo(() => buildDefaultValues(data, profile), [data, profile]);

  const client = useApolloClient();

  // Validates all four submission-scoped sections in one request. Each
  // submission-scoped section is sent only when its gate is "Yes" (or unanswered
  // → omitted); §14 always sends the consolidation input (gate + sites). The
  // resolver returns the combined issues, with §14 already filtered to its keys.
  const validator = useCallback(
    async (formData: FilingNotificationsFormData): Promise<Issue[]> => {
      const res = await client.query({
        query: VALIDATE_SUBMISSION_RCRA_FILING_NOTIFICATIONS,
        variables: {
          submissionId,
          input: {
            episodicEvent: formData.episodicGate === true ? buildEpisodicInput(formData) : null,
            siteClosure: formData.closureGate === true ? buildClosureInput(formData) : null,
            hsmNotification: formData.hsmGate === true ? buildHsmInput(formData) : null,
            consolidation: buildConsolidationInput(formData),
          },
        },
        fetchPolicy: "no-cache",
      });
      return res.data?.submissionRcraFilingNotificationsInputValidator ?? [];
    },
    [client, submissionId]
  );

  const form = useValidatingForm<FilingNotificationsFormData>(defaultValues, [], validator, [], {
    isDisplayOnly,
  });

  const { handleSubmit, issues, trigger } = form;

  // Validate once the submission and profile data have loaded so the compliance
  // gates (e.g. a non-LQG site that answered Yes to consolidation/closure)
  // surface on load — not only after the first edit. `useValidatingForm` only
  // validates on change, so without this the footer/issue list starts empty.
  useEffect(() => {
    if (data) {
      void trigger();
    }
  }, [data, profile, trigger]);

  const persist = useCallback(
    async (formData: FilingNotificationsFormData): Promise<boolean> => {
      try {
        await Promise.all([
          formData.episodicGate === true
            ? upsertEpisodic({
                variables: { submissionId, input: buildEpisodicInput(formData) },
              })
            : clearEpisodic({ variables: { submissionId } }),
          formData.closureGate === true
            ? upsertClosure({ variables: { submissionId, input: buildClosureInput(formData) } })
            : clearClosure({ variables: { submissionId } }),
          formData.hsmGate === true
            ? upsertHsm({ variables: { submissionId, input: buildHsmInput(formData) } })
            : clearHsm({ variables: { submissionId } }),
          // §14 saves in two scopes: the `isLqgConsolidator` gate persists on the
          // facility RCRA profile (a narrow input that leaves the rest of the
          // profile untouched), while the sites persist on the submission
          // (replace-semantics). Both always run — the gate value is the saved
          // answer, not a clear/skip.
          upsertRcraProfile({
            variables: { facilityId, input: buildConsolidationGateInput(formData) },
          }),
          upsertConsolidation({
            variables: { submissionId, input: buildConsolidationInput(formData) },
          }),
        ]);
        return true;
      } catch {
        alerts.error("Error saving filing notifications.");
        return false;
      }
    },
    [
      submissionId,
      facilityId,
      upsertEpisodic,
      clearEpisodic,
      upsertClosure,
      clearClosure,
      upsertHsm,
      clearHsm,
      upsertRcraProfile,
      upsertConsolidation,
      alerts,
    ]
  );

  const onSave = useCallback(
    () =>
      new Promise<boolean>((resolve) => {
        void handleSubmit(
          async (formData) => resolve(await persist(formData)),
          () => resolve(false)
        )();
      }),
    [handleSubmit, persist]
  );

  return (
    <Stack spacing={2}>
      <StepHeader title="Filing Notifications" />
      <Typography variant="body2" color="text.secondary">
        Optional 8700-12 notifications. Answer each question below; sections you answer "No" are not
        included on the submission.
      </Typography>
      <FormProvider {...form}>
        <form>
          <Stack spacing={5}>
            <EpisodicGenerationSection facilityId={facilityId} />
            <LqgConsolidationSection facilityId={facilityId} />
            <LqgSiteClosureSection />
            <HsmNotificationSection />
          </Stack>
          {canEdit && (
            <StepSaveFooter
              submissionId={submissionId}
              step={SubmissionStepType.FilingNotifications}
              onSave={onSave}
              saving={saving}
              disabled={hasCriticalIssues(issues)}
              leftActions={<IssueListButton issues={issues} />}
            />
          )}
        </form>
      </FormProvider>
    </Stack>
  );
}
